import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Archive, ArrowRight, CheckCircle, Trash, ClipboardText } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import VesselPostOpModal from "../components/VesselPostOpModal";

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, { hour12: false });
}

export default function History() {
    const { user } = useAuth();
    const [vessels, setVessels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const [managingVessel, setManagingVessel] = useState(null);

    const isAdmin = user?.role === "admin";
    const canManage = ["admin", "finance", "safety", "client_liaison", "fleet_manager", "manager"].includes(user?.role);

    const load = async () => {
        try {
            const { data } = await api.get("/vessels", { params: { status: "completed" } });
            setVessels(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const remove = async (v) => {
        const confirmed = window.confirm(
            `Permanently delete "${v.vessel_name}" and all its reports, delays, remarks, and messages?\n\nThis cannot be undone.`
        );
        if (!confirmed) return;
        setDeletingId(v.id);
        try {
            await api.delete(`/vessels/${v.id}`);
            setVessels((prev) => prev.filter((x) => x.id !== v.id));
            toast.success(`${v.vessel_name} deleted`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="min-h-screen">
            <Header />
            <main className="max-w-[1200px] mx-auto px-5 py-8">
                <div className="mb-6">
                    <div className="uppercase-label text-[var(--grindrod-red)]">Archive</div>
                    <h1 className="font-display font-black text-3xl text-[var(--grindrod-navy)]">
                        Completed Vessels
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Full performance history for every vessel that has finished loading.
                    </p>
                </div>

                {loading ? (
                    <div className="text-slate-500" data-testid="history-loading">Loading…</div>
                ) : vessels.length === 0 ? (
                    <div className="g-card p-10 text-center" data-testid="history-empty">
                        <Archive size={44} weight="duotone" className="mx-auto text-slate-300 mb-3" />
                        <div className="font-display font-bold text-xl text-[var(--grindrod-navy)]">No completed vessels yet</div>
                        <p className="text-slate-500 text-sm mt-1">Completed vessels will appear here for historical review.</p>
                    </div>
                ) : (
                    <div className="g-card overflow-x-auto">
                        <table className="g-table" data-testid="history-table">
                            <thead>
                                <tr>
                                    <th>Vessel</th>
                                    <th>Client</th>
                                    <th>Product</th>
                                    <th>Tonnage</th>
                                    <th>Started</th>
                                    <th>Completed</th>
                                    <th className="text-right pr-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vessels.map((v) => (
                                    <tr key={v.id} data-testid={`history-row-${v.id}`}>
                                        <td className="font-semibold text-[var(--grindrod-navy)]">{v.vessel_name}</td>
                                        <td>{v.client_name}</td>
                                        <td>{v.product}</td>
                                        <td className="tabular">{v.tonnage.toLocaleString()}</td>
                                        <td className="text-xs text-slate-500">{fmtDate(v.start_datetime)}</td>
                                        <td className="text-xs text-slate-500">
                                            <span className="inline-flex items-center gap-1 text-[var(--grindrod-green)] font-semibold">
                                                <CheckCircle size={12} weight="fill" /> {fmtDate(v.completed_at)}
                                            </span>
                                        </td>
                                        <td className="text-right pr-4">
                                            <div className="inline-flex gap-1.5 flex-wrap justify-end">
                                                <Link
                                                    to={`/vessel/${v.id}`}
                                                    className="btn-outline !py-1 !px-2 text-xs inline-flex items-center gap-1"
                                                    data-testid={`history-view-${v.id}`}
                                                >
                                                    View <ArrowRight size={12} />
                                                </Link>
                                                <Link
                                                    to={`/vessel/${v.id}/certificate`}
                                                    className="btn-navy !py-1 !px-2 text-xs inline-flex items-center gap-1"
                                                    data-testid={`history-cert-${v.id}`}
                                                >
                                                    Report / Export
                                                </Link>
                                                {canManage && (
                                                    <button
                                                        onClick={() => setManagingVessel(v)}
                                                        className="btn-outline !py-1 !px-2 text-xs inline-flex items-center gap-1 !border-[#B36B00] !text-[#B36B00]"
                                                        data-testid={`history-manage-${v.id}`}
                                                        title="Manage costs, manpower, fleet, safety, client feedback"
                                                    >
                                                        <ClipboardText size={12} /> Manage data
                                                    </button>
                                                )}
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => remove(v)}
                                                        disabled={deletingId === v.id}
                                                        className="btn-outline !py-1 !px-2 text-xs inline-flex items-center gap-1 !border-[var(--grindrod-red)] !text-[var(--grindrod-red)] disabled:opacity-50"
                                                        data-testid={`history-delete-${v.id}`}
                                                        title="Permanently delete vessel history"
                                                    >
                                                        <Trash size={12} />
                                                        {deletingId === v.id ? "Deleting…" : "Delete"}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
            {managingVessel && (
                <VesselPostOpModal vessel={managingVessel} onClose={() => setManagingVessel(null)} onChanged={load} />
            )}
        </div>
    );
}
